import {
  Box,
  Card,
  Flex,
  Icon,
  Text,
  Input,
  Button,
  Heading,
  CardBody,
  FormLabel,
  IconButton,
  FormControl,
  CurrencyInput,
  FormHelperText
} from '@homebotapp/hb-react-component-catalog'
import { useEffect, useState } from 'react'
import { useIntl, defineMessages } from 'react-intl'
import { formatCurrency } from '../../../../../../helpers/formatters'
import { NATIONAL_AVG_MONTHLY_PROPERTY_TAX_RATE } from '../../Utilities/EstimatedMonthlyPaymentConstants'

export const MSG = defineMessages({
  propertyTaxes: {
    id: 'EstimatedMonthlyPaymentBreakdown.propertyTaxes',
    defaultMessage: 'Property taxes'
  },
  propertyTaxesDescription: {
    id: 'EstimatedMonthlyPaymentBreakdown.propertyTaxesDescription',
    defaultMessage: 'Based on historical data'
  },
  hide: {
    id: 'EstimatedMonthlyPaymentBreakdown.propertyTaxesHide',
    defaultMessage: 'Hide editable fields'
  },
  show: {
    id: 'EstimatedMonthlyPaymentBreakdown.propertyTaxesShow',
    defaultMessage: 'Show editable fields'
  },
  monthlyAmount: {
    id: 'EstimatedMonthlyPaymentBreakdown.monthlyAmount',
    defaultMessage: 'Monthly amount'
  },
  zipCode: {
    id: 'EstimatedMonthlyPaymentBreakdown.propertyTaxesZipCode',
    defaultMessage: 'ZIP code'
  },
  zipCodeDesc: {
    id: 'EstimatedMonthlyPaymentBreakdown.propertyTaxesZipCodeDesc',
    defaultMessage: 'Get a better estimate for your area'
  },
  natlAvgCardHeader: {
    id: 'EstimatedMonthlyPaymentBreakdown.propertyTaxesNatlAvgCardHeader',
    defaultMessage: 'Using national average'
  },
  natlAvgCardBody: {
    id: 'EstimatedMonthlyPaymentBreakdown.propertyTaxesNatlAvgCardBody',
    defaultMessage:
      'This is the national average for property taxes based on the home price. Enter your desired ZIP code to get a more accurate estimate for your area.'
  }
})

export interface EstimatedMonthlyPaymentBreakdownPropertyTaxesProps {
  zipCode: string
  homePrice: string
  propertyTaxes: string
  isCalculatorsPage?: boolean
  setZipCode: (value: string) => void
  setPropertyTaxes: (value: string) => void
  setHasUsedMortgageCalculator: (value: boolean) => void
}

export const EstimatedMonthlyPaymentBreakdownPropertyTaxes = ({
  zipCode,
  homePrice,
  setZipCode,
  propertyTaxes,
  setPropertyTaxes,
  isCalculatorsPage,
  setHasUsedMortgageCalculator
}: EstimatedMonthlyPaymentBreakdownPropertyTaxesProps) => {
  const intl = useIntl()
  const [showNatlAvgCard, setShowNatlAvgCard] = useState(false)
  const [showEditableFields, setShowEditableFields] = useState(false)

  useEffect(() => {
    if (
      Math.trunc(Number(propertyTaxes)) ===
      Math.trunc(Math.round((NATIONAL_AVG_MONTHLY_PROPERTY_TAX_RATE * Number(homePrice)) / 12))
    ) {
      setShowNatlAvgCard(true)
      setShowEditableFields(true)
    } else {
      setShowNatlAvgCard(false)
    }
  }, [propertyTaxes, homePrice])

  return (
    <Card p={3}>
      <Button
        w='100%'
        variant='link'
        display='block'
        textAlign='left'
        fontWeight='inherit'
        textColor='currentColor'
        _hover={{ textDecoration: 'none' }}
        onClick={() => setShowEditableFields(!showEditableFields)}
      >
        <Flex align='center' justify='space-between'>
          <Flex gap={2} align='center'>
            {/* @TODO:DESIGNSYSTEM - add purple ramp */}
            {/* @ts-ignore */}
            <Box bg='#9770F7' width={3} height={3} borderRadius='50%' />
            <Heading as='h4' size={['2xs', 'xs', 'sm']}>
              {intl.formatMessage(MSG.propertyTaxes)}
            </Heading>
          </Flex>
          <Flex>
            <Text
              mb={0}
              color='neutral.900'
              size={['xs', 'sm', 'md']}
              fontWeight='semibold'
              style={{ fontVariant: 'tabular-nums' }}
            >
              {formatCurrency(propertyTaxes)}
            </Text>
            <IconButton
              size='xs'
              variant='link'
              onClick={() => setShowEditableFields(!showEditableFields)}
              aria-label={intl.formatMessage(showEditableFields ? MSG.hide : MSG.show)}
              icon={<Icon height={2} name='play' transform={`rotate(${showEditableFields ? -90 : 90}deg)`} />}
              tracking={{
                guid: 'rFt1SGmgVuBsj4hR523Vkh',
                ui_context: isCalculatorsPage ? 'Mortgage Calculator Page' : 'Listing Details Page Mortgage Calculator',
                descriptor: `Property Taxes ${showEditableFields ? 'Hide' : 'Show'}`
              }}
            />
          </Flex>
        </Flex>
        <Text ml={5} as='span' display='block' size={['2xs', 'xs', 'sm']}>
          {intl.formatMessage(MSG.propertyTaxesDescription)}
        </Text>
      </Button>
      {showEditableFields && (
        <Box>
          {showNatlAvgCard && (
            <>
              {/* @ts-ignore */}
              <Card mt={4} backgroundColor='var(--hb-colors-warning-50) !important'>
                <CardBody p={3} display='flex' gap={3}>
                  <Icon mt={1} color='warning.700' name='info-circle-fa' />
                  <Box>
                    <Heading mb={0} as='h5' size='2xs' textAlign='left'>
                      {intl.formatMessage(MSG.natlAvgCardHeader)}
                    </Heading>
                    <Text size='xs'>{intl.formatMessage(MSG.natlAvgCardBody)}</Text>
                  </Box>
                </CardBody>
              </Card>
            </>
          )}
          <FormControl mt={4}>
            <FormLabel size='xs'>{intl.formatMessage(MSG.zipCode)}</FormLabel>
            <Input
              size='sm'
              type='number'
              name='zipCode'
              value={zipCode}
              maxWidth='250px'
              onChange={e => setZipCode(e.target.value)}
              tracking={{
                descriptor: 'Zip Code',
                guid: '6XHfcAh9vZ8xQj8Nc2R3ky',
                ui_context: isCalculatorsPage ? 'Mortgage Calculator Page' : 'Listing Details Page Mortgage Calculator'
              }}
            />
            <FormHelperText>
              <Text mb={0} size='3xs'>
                {intl.formatMessage(MSG.zipCodeDesc)}
              </Text>
            </FormHelperText>
          </FormControl>
          <FormControl mt={4}>
            <FormLabel size='xs'>{intl.formatMessage(MSG.monthlyAmount)}</FormLabel>
            <CurrencyInput
              size='sm'
              maxWidth='250px'
              decimalScale={2}
              name='propertyTaxes'
              value={propertyTaxes}
              // @ts-ignore
              onChange={val => setPropertyTaxes(val)}
              onFocus={() => setHasUsedMortgageCalculator(true)}
              tracking={{
                descriptor: 'Property Taxes',
                guid: 'bWp13f4waN2hDGSM1PXz3c',
                ui_context: isCalculatorsPage ? 'Mortgage Calculator Page' : 'Listing Details Page Mortgage Calculator'
              }}
            />
          </FormControl>
        </Box>
      )}
    </Card>
  )
}
